// Types for the Data Integration feature — merging two projects' DQD data
// files into a NEW data-only project via the conductor `/data-integrate`
// endpoint. Wire-format fields (report + log) are snake_case to mirror the
// engine response verbatim.

// The merge operation: append cases (add) or horizontal join by ID path (connect).
export enum INTEGRATION_OPERATION {
  ADD = "add",
  CONNECT = "connect",
}

// Which variables to keep when appending cases (add).
export enum ADD_KEEP_OPTION {
  ALL = "all",
  ORIGINAL = "original",
  ADD = "add",
  BOTH = "both",
}

// Which respondents to keep when joining by ID (connect).
export enum CONNECT_JOIN_OPTION {
  ALL = "all",
  BOTH = "both",
  ORIGINAL = "original",
}

// A single per-variable entry in the integration report log.
export interface IntegrationLogEntry {
  path: string
  action:
    | "ADDED"
    | "MERGED"
    | "SKIPPED"
    | "ERROR"
    | "CONNECTED"
    | "DEDUPLICATED"
    | "RENAMED"
  message: string
  from_original: boolean
  from_add?: boolean
  from_connect?: boolean
}

// The engine's merge report (counts + per-variable log). Count fields are
// operation-specific: the `add` block for append, the `connect` block for join.
export interface IntegrationReport {
  operation: INTEGRATION_OPERATION
  // add
  original_cases?: number
  added_cases?: number
  merged_cases?: number
  variables_original_only?: number
  variables_add_only?: number
  variables_both?: number
  variables_skipped?: number
  // connect
  original_respondents?: number
  connect_respondents?: number
  matched_respondents?: number
  unmatched_original?: number
  unmatched_connect?: number
  output_respondents?: number
  variables_connect_only?: number
  variables_deduplicated?: number
  variables_renamed?: number
  log: IntegrationLogEntry[]
}

// Parameters for a data-integration request.
export interface IntegrateDataParams {
  baseId: string
  baseOwner: string
  addId: string
  addOwner: string
  operation: INTEGRATION_OPERATION
  keep?: ADD_KEEP_OPTION
  join?: CONNECT_JOIN_OPTION
  idPath?: string // "Section//Variable"
  name: string // new project name
  // Name of a saved query run against EACH project before the merge, so both waves
  // are built from the same definitions. Deriving after a merge instead leaves the
  // earlier wave carrying blanks.
  deriveQuery?: string
  // Which project holds that saved query. Defaults to the base project.
  querySourceId?: string
  querySourceOwner?: string
  // Stamps an INT variable WAVE on each side: base = firstWave, second = firstWave + 1.
  // Append (add) only — connect joins the same respondents, so per-side wave numbers
  // would collide into one column.
  firstWave?: number
  // Runs each project's own Run on load scripts first, so the merged file carries the
  // saved variables as ordinary variables. False merges the data files as stored.
  applyOnLoad?: boolean
}

// A content-addressed lineage snapshot captured at merge time, fetched back through
// /deliverable-lineage.
export interface IntegrationLineageRef {
  hash: string
  nodeCount?: number
  edgeCount?: number
}

// Which step of the merge failed. The derive/wave stages are the ones that need the
// log surfaced: the engine reports a query-script error in its log only, never as an
// API error, so without the server-side marker check the merge would have silently
// produced a wave carrying blanks.
export type IntegrationFailureStage =
  | "onload-base"
  | "onload-add"
  | "load-base"
  | "derive-base"
  | "wave-base"
  | "dump-base"
  | "load-add"
  | "derive-add"
  | "wave-add"
  | "dump-add"

export interface IntegrationFailure {
  stage: IntegrationFailureStage
  // The derive script, or for an onload stage the Run on load script that stopped.
  query?: string
  project_id?: string
  // The engine's script log. The LAST entry is the failure.
  log: string[]
}

export interface IntegrationOnLoad {
  base: string[]
  add: string[]
  // Set when the two projects ran different scripts, or (append) only one ran any.
  warning?: string
}

// The conductor response for a successful data integration.
export interface IntegrateDataResponse {
  message: string
  project_id: string
  operation: INTEGRATION_OPERATION
  report?: IntegrationReport
  derive?: {
    query?: string
    base_wave?: number
    add_wave?: number
  }
  // The Run on load scripts each project ran before the merge. Present only when at
  // least one ran.
  onload?: IntegrationOnLoad
  lineage?: {
    base?: IntegrationLineageRef
    add?: IntegrationLineageRef
  }
  // False when the merge succeeded but its provenance record could not be written.
  recorded?: boolean
}
